"use client";

import React, { useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useNotifications } from "@/hooks/useNotifications";
import { Iconly } from "@/components/ui/iconly-wrapper";
import { toast } from "@/components/ui/sonner";

const STUDENT_SHELF = [
  { label: "Library", href: "/library", iconName: "Library" },
  { label: "Updates", href: "/notifications", iconName: "Notification" },
  { label: "Profile", href: "/profile", iconName: "User" },
];

const PUBLISHER_SHELF = [
  { label: "Library", href: "/publisher/library", iconName: "Library" },
  { label: "Submissions", href: "/publisher/submissions", iconName: "Paper" },
  { label: "Profile", href: "/publisher/profile", iconName: "User" },
];

export function MobileDock() {
  const location = useLocation();
  const navigate = useNavigate();
  const { unreadCount, inboxUnreadCount } = useNotifications();
  const totalUnread = unreadCount + inboxUnreadCount;
  
  const pathname = location.pathname;
  
  // 1. CONTEXT DETECTION (Strict Split)
  const isPublisherContext = pathname.startsWith('/publisher');
  
  // 2. DEPTH DETECTION
  const historyIndex = typeof window !== "undefined" && typeof window.history.state?.idx === "number"
    ? window.history.state.idx
    : 0;

  // Root paths for each context
  const studentRoots = ['/dashboard', '/library', '/notifications', '/profile'];
  const publisherRoots = ['/publisher', '/publisher/library', '/publisher/submissions', '/publisher/profile'];
  
  const currentRoots = isPublisherContext ? publisherRoots : studentRoots;
  
  // Show back button if it's a deep page OR if we are on Notifications but came from somewhere else
  const isDeepPage = (!currentRoots.includes(pathname) && pathname !== '/') || (pathname === '/notifications' && historyIndex > 0);

  // 3. ANCHOR CONFIG
  const anchorHomePath = isPublisherContext ? "/publisher" : "/dashboard";
  const anchorHomeIcon = isPublisherContext ? "Category" : "Home";
  
  // 4. SHELF CONFIG
  const currentShelf = isPublisherContext ? PUBLISHER_SHELF : STUDENT_SHELF;

  const handleBackAction = () => {
    const historyIndex = typeof window !== "undefined" && typeof window.history.state?.idx === "number"
      ? window.history.state.idx
      : 0;
    
    if (historyIndex > 0) {
      navigate(-1);
    } else {
      navigate(anchorHomePath, { replace: true });
    }
  };

  // Long press handler for going all the way home
  const longPressTimer = useRef<number | null>(null);
  const handleTouchStart = () => {
    longPressTimer.current = window.setTimeout(() => {
      navigate(anchorHomePath);
      toast.info("Back to Home", { duration: 1000 });
    }, 600);
  };
  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  return (
    <div className="w-full">
      <nav className="dock-glass flex items-center gap-1.5 p-2 rounded-full">
        
        {/* --- THE ANCHOR (Home or Back) --- */}
        <div className="relative ml-1 h-12 w-12 flex items-center justify-center">
          <AnimatePresence initial={false}>
            {isDeepPage ? (
              <motion.button
                key="back-anchor"
                layoutId="anchor-shape"
                onClick={handleBackAction}
                onMouseDown={handleTouchStart}
                onMouseUp={handleTouchEnd}
                onMouseLeave={handleTouchEnd}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                whileTap={{ scale: 0.88 }}
                className="absolute inset-0 flex items-center justify-center rounded-full bg-[var(--athr-brand)] text-white shadow-lg shadow-[var(--athr-brand-soft)] focus:outline-none"
                transition={{ type: "spring", bounce: 0.35, duration: 0.6 }}
              >
                <motion.div 
                  initial={{ opacity: 0, scale: 0.6, rotate: -45, x: -1 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0, x: -1 }}
                  exit={{ opacity: 0, scale: 0.6, rotate: 45, x: -1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="flex items-center justify-center"
                >
                  <Iconly name="CaretLeft" set="curved" size="medium" />
                </motion.div>
                
                {/* Subtle Ping Animation on Entrance */}
                <motion.span 
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1.5, opacity: 0 }}
                  transition={{ duration: 0.8, repeat: 1 }}
                  className="absolute inset-0 rounded-full bg-white/20 pointer-events-none"
                />
              </motion.button>
            ) : (
              <Link to={anchorHomePath} className="absolute inset-0 focus:outline-none">
                <motion.div
                  key="home-anchor"
                  layoutId="anchor-shape"
                  whileTap={{ scale: 0.92 }}
                  className={cn(
                    "flex h-full w-full items-center justify-center rounded-full transition-colors duration-300",
                    pathname === anchorHomePath 
                      ? "bg-[var(--athr-brand-soft-2)]/50 text-[var(--athr-brand)]" 
                      : "bg-transparent text-[var(--athr-text-tertiary)] hover:text-[var(--athr-text-secondary)]"
                  )}
                  transition={{ type: "spring", bounce: 0.35, duration: 0.6 }}
                >
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                  >
                    <Iconly name={anchorHomeIcon} set={pathname === anchorHomePath ? "bold" : "light"} size="medium" />
                  </motion.div>
                </motion.div>
              </Link>
            )}
          </AnimatePresence>
        </div>

        {/* --- THE SPACE (Divider) --- */}
        <div className="h-8 w-[1.5px] bg-[var(--athr-border-soft)]/50 mx-1.5 rounded-full" />

        {/* --- THE SHELF (Context-Specific Groups) --- */}
        <div className="flex flex-1 items-center justify-around pr-2">
          {currentShelf.map((item) => {
            const isActive = pathname === item.href || (pathname.startsWith(item.href) && item.href !== "/");
            const showBadge = !isPublisherContext && item.label === "Updates" && totalUnread > 0;
            
            return (
              <Link
                key={item.href}
                to={item.href}
                className="relative flex flex-col items-center justify-center group flex-1 outline-none py-2"
              >
                <div className={cn(
                  "relative z-10 transition-all duration-500 flex flex-col items-center justify-center",
                  isActive ? "text-[var(--athr-brand)] transform translate-y-[-2px]" : "text-[var(--athr-text-tertiary)] hover:text-[var(--athr-text-secondary)]"
                )}>
                  <div className="relative">
                    <Iconly 
                      name={item.iconName} 
                      set={isActive ? "bold" : "light"} 
                      size="medium"
                      animate={isActive}
                    />
                    
                    {showBadge && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full border-[1.5px] border-white shadow-sm shadow-red-500/20" />
                    )}
                  </div>
                </div>
                
                {isActive && (
                  <motion.div
                    layoutId="activeDockIndicator"
                    className="absolute bottom-1 w-5 h-1 bg-[var(--athr-brand)] rounded-full z-10 shadow-[0_4px_12px_var(--athr-brand-soft)]"
                    transition={{ type: "spring", bounce: 0.3, duration: 0.6 }}
                  />
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
